import type { IrReport } from './types';

export const G4_IR_REPORTS: IrReport[] = [
  /* G4R1 */
  {
    key: 'G4R1',
    group: 'G4',
    rCode: 'R1',
    pillar: 'Governance & Structure',
    stage: 'Critical Risk Stage',
    tone: 'red',
    profile: 'Equity Novice',
    tagline: "You are an Equity Novice, driven by vision but vulnerable to structural risks.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You are at the very beginning of your governance and structure journey. At this stage, your company likely has little to no formal governance in place: ownership agreements may be unclear, roles and responsibilities are undefined, and there's no structured compliance or policies. This is a critical risk stage because weak governance can derail even the best product or market fit. Disputes among founders, unclear equity splits, or missing compliance documentation are among the top reasons startups fail before they can scale. You are an Equity Novice, driven by vision but vulnerable to structural risks. Your next priority is to lay down the legal and organizational foundation that will protect your company and build investor confidence.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: "Your diagnostic responses indicate:" },
          {
            kind: 'bullets',
            items: [
              "No formal shareholder or founder agreements in place.",
              "Cap table may be incomplete or inaccurate.",
              "Roles, responsibilities, or decision rights are not clearly defined.",
              "No compliance practices (board structure, policies, contracts).",
              "Governance is ad hoc or non-existent.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This puts your company in a fragile state, where disputes or diligence failures could stop your progress.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Clarify Equity & Ownership",
                sub: [
                  "Draft and formalize founder agreements.",
                  "Build a clean, accurate cap table.",
                  "Define vesting schedules to align founder commitment.",
                ],
              },
              {
                title: "Define Roles & Decision Rights",
                sub: [
                  "Assign clear responsibilities for each founder/executive.",
                  "Document decision-making processes (who decides what).",
                  "Avoid overlap and ambiguity that can cause conflicts.",
                ],
              },
              {
                title: "Establish Governance Basics",
                sub: [
                  "Incorporate your company if not already.",
                  "Create a simple board/advisory structure.",
                  "Begin drafting key policies (employment contracts, IP assignment).",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "At this stage, your platform provides governance starter tools:",
          },
          {
            kind: 'bullets',
            items: [
              "Equity Designer Lite: cap table starter + SAFE template",
              "Founder Agreement Checklist: key terms every co-founder deal should cover",
              "Governance Basics Pack: board outline, role definitions, policy starter templates",
              "IP Assignment Template: ensure ownership of company IP",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "Without strong governance, you're building on sand. Even the best startup ideas can collapse if equity is disputed, IP isn't protected, or compliance is overlooked. Investors won't engage without clarity here. For them, governance is a trust signal: it shows you are serious, professional, and prepared for growth.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are an Equity Novice.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "Your product and vision matter, but without structure, your startup is at risk.",
          },
          {
            kind: 'bullets',
            items: [
              "Use the Equity Designer Lite to clean up your cap table.",
              "Draft founder agreements this month.",
              "Formalize IP ownership with the IP Assignment Template.",
              "Book a Governance Kickstart Session with a Conncct advisor to get structured.",
            ],
          },
          {
            kind: 'callout',
            text: "Your next milestone: progress to G4R2 Structure Starter, where governance basics are in place and you begin to look credible to outside stakeholders.",
            tone: 'green',
          },
        ],
      },
    ],
  },

  /* G4R2 */
  {
    key: 'G4R2',
    group: 'G4',
    rCode: 'R2',
    pillar: 'Governance & Structure',
    stage: 'Very High Risk Stage',
    tone: 'orange',
    profile: 'Structure Starter',
    tagline: "You are a Structure Starter, you've begun the journey, but now you must close the gaps.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've started to put down roots in governance and structure. Maybe you've drafted a founder agreement, built a cap table, or incorporated your company. These are important first steps: many startups never get this far. However, your governance remains fragile and incomplete. Investors, partners, or even co-founders would still see red flags: unclear ownership, missing compliance, or lack of formal processes. Your priority now is to stabilize your foundation so that your company looks credible and secure. You are a Structure Starter, you've begun the journey, but now you must close the gaps.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: "Your diagnostic results suggest:" },
          {
            kind: 'bullets',
            items: [
              "Some governance artifacts exist (incorporation, basic agreements, or cap table), but not all.",
              "Cap table may lack clarity on dilution, vesting, or convertible notes.",
              "Board or advisory roles are undefined.",
              "Policies (employment contracts, ESOP, compliance) are missing or informal.",
              "Governance is reactive, not proactive.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This means you've taken positive steps, but you're still at very high risk if challenged by investors, auditors, or legal disputes.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Finalize Equity Structures",
                sub: [
                  "Ensure your cap table is complete, accurate, and investor-friendly.",
                  "Add vesting schedules and handle convertible notes/SAFEs correctly.",
                  "Clarify any equity given to advisors or contractors.",
                ],
              },
              {
                title: "Formalize Governance Roles",
                sub: [
                  "Define roles for a simple board or advisory council.",
                  "Document decision-making processes and reporting lines.",
                  "Assign accountability for compliance tasks.",
                ],
              },
              {
                title: "Build Essential Policies",
                sub: [
                  "Create standard employment agreements with IP clauses.",
                  "Draft a basic governance policy pack (HR, finance, compliance basics).",
                  "Begin drafting an ESOP framework for future talent attraction.",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "At this stage, your platform unlocks governance starter packs:",
          },
          {
            kind: 'bullets',
            items: [
              "Governance Basics Pack: board/advisor role templates, policy outline",
              "Cap Table Template (Pro): includes vesting, convertible notes, and dilution tracking",
              "Employment Agreement Starter Kit: templates with IP & confidentiality clauses",
              "Convertible Note Guide: to clarify obligations and conversion terms",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've taken important steps, but governance isn't about paperwork: it's about trust, clarity, and protection. At this stage, your company still looks fragile to outsiders. Investors will hesitate unless you can prove ownership and compliance are properly managed. The good news: building credibility doesn't require perfection, just completion and consistency.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are a Structure Starter.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "You've begun your governance journey. Now you need to close the gaps before they become risks.",
          },
          {
            kind: 'bullets',
            items: [
              "Use the Cap Table Template (Pro) to finalize equity structures.",
              "Formalize your governance with the Governance Basics Pack.",
              "Standardize contracts with the Employment Agreement Starter Kit.",
              "Book a Governance Review Call with a Conncct advisor to check compliance gaps.",
            ],
          },
          {
            kind: 'callout',
            text: "Your next milestone: progress to G4R3 Governance Builder, where your governance practices become robust enough to support growth and attract early investors.",
            tone: 'green',
          },
        ],
      },
    ],
  },

  /* G4R3 */
  {
    key: 'G4R3',
    group: 'G4',
    rCode: 'R3',
    pillar: 'Governance & Structure',
    stage: 'High Risk Stage',
    tone: 'orange',
    profile: 'Governance Builder',
    tagline: "You are a Governance Builder, laying the framework that supports both credibility and growth.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've made steady progress: your company has a formal structure in place (incorporation, basic cap table, some contracts, possibly an advisory board). You're no longer in the \"wild west\" of governance. However, you're still at a high-risk stage. Your governance is stronger than a starter's, but not yet robust enough for investor diligence or serious scaling. The challenge now is to move from partial structure to reliable governance. You are a Governance Builder, laying the framework that supports both credibility and growth.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: "Your diagnostic results highlight:" },
          {
            kind: 'bullets',
            items: [
              "A cleaner cap table exists, but it may lack advanced modeling (e.g., ESOP, dilution scenarios).",
              "Founder agreements and IP assignment are in place, but not all employee or contractor contracts are standardized.",
              "Basic board/advisory structures may exist but lack formality (no minutes, irregular meetings).",
              "Governance documents exist but are inconsistent or incomplete.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This is progress, but investors will still spot red flags.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Strengthen Equity Management",
                sub: [
                  "Finalize ESOP design (size, terms, vesting).",
                  "Model dilution scenarios for future funding rounds.",
                  "Ensure equity agreements are consistent and enforceable.",
                ],
              },
              {
                title: "Build Reliable Governance Processes",
                sub: [
                  "Formalize board or advisory meetings (set cadence, agendas, and minutes).",
                  "Create a compliance calendar (tax, filings, IP renewals).",
                  "Assign accountability for governance oversight.",
                ],
              },
              {
                title: "Standardize Legal & HR Documents",
                sub: [
                  "Implement standard contracts for employees, advisors, and freelancers.",
                  "Draft or refine company policies (HR, ethics, anti-bribery, ESG basics).",
                  "Ensure all IP and data rights are assigned to the company.",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "At this stage, your platform unlocks professional governance kits:",
          },
          {
            kind: 'bullets',
            items: [
              "Cap Table Pro Toolkit: includes ESOP and dilution modeling",
              "Governance Dashboard Lite: track meetings, filings, and compliance deadlines",
              "Contract Library (Pro): standardized agreements for staff, advisors, contractors",
              "Policy Builder Starter Pack: templates for HR, ethics, and compliance",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've moved beyond ad hoc governance. You're building the scaffolding of a credible company. But credibility requires consistency. Half-completed policies or unclear equity still signal weakness to investors. The key here is discipline: treat governance as seriously as product and sales. Done well, governance becomes a strategic advantage, building trust with investors, partners, and your own team.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are a Governance Builder.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "You're creating the backbone of your company. The stronger it is, the faster you can scale.",
          },
          {
            kind: 'bullets',
            items: [
              "Use the Cap Table Pro Toolkit to finalize ESOP and dilution models.",
              "Implement the Governance Dashboard Lite to track board/compliance activities.",
              "Standardize all contracts using the Contract Library (Pro).",
              "Book a Governance Health Check with a Conncct advisor.",
            ],
          },
          {
            kind: 'callout',
            text: "Your next milestone: progress to G4R4 Structure Professional, where governance becomes not just functional but investor-grade.",
            tone: 'green',
          },
        ],
      },
    ],
  },

  /* G4R4 */
  {
    key: 'G4R4',
    group: 'G4',
    rCode: 'R4',
    pillar: 'Governance & Structure',
    stage: 'Medium Risk Stage',
    tone: 'yellow',
    profile: 'Structure Professional',
    tagline: "You are a Structure Professional, a founder who has turned governance into a strength but must now mature it into a system that scales.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've moved beyond basic governance: your company now has a clean cap table, formal agreements, and governance practices that show professionalism. Investors, partners, and employees will take you more seriously because your structure signals discipline. At this stage, however, your governance is not yet fully institutionalized. While the basics are solid, you'll need to upgrade consistency, compliance, and reporting to be fully investor-grade. You are a Structure Professional, a founder who has turned governance into a strength but must now mature it into a system that scales.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: "Your diagnostic results show:" },
          {
            kind: 'bullets',
            items: [
              "Cap table is clear and accurate, potentially with ESOP planning underway.",
              "Founder and employee agreements are standardized, covering IP and confidentiality.",
              "Board or advisory meetings exist, though cadence or documentation may not yet be fully formalized.",
              "Policies and compliance are drafted but not systematically tracked.",
              "Governance is credible, but not yet scalable or institutionalized.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This is why you're seen as \"medium risk\": solid foundations, but still gaps before reaching investor-grade excellence.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Institutionalize Governance Processes",
                sub: [
                  "Move from informal board meetings to structured sessions with agendas and minutes.",
                  "Set a governance calendar for filings, reporting, and compliance.",
                  "Create accountability by assigning governance tasks to a lead (CFO, COO, or legal).",
                ],
              },
              {
                title: "Expand Equity Management",
                sub: [
                  "Finalize and implement ESOP for key hires.",
                  "Model future funding rounds for dilution impact.",
                  "Ensure advisor/contractor equity is properly documented.",
                ],
              },
              {
                title: "Strengthen Compliance & Policies",
                sub: [
                  "Upgrade policies to cover ESG basics, anti-bribery, and data privacy.",
                  "Ensure contracts and agreements are legally enforceable across all jurisdictions.",
                  "Document compliance processes for due diligence readiness.",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "At this stage, your platform provides professional-grade governance resources:",
          },
          {
            kind: 'bullets',
            items: [
              "Governance Dashboard Pro: compliance calendar + board reporting templates",
              "Equity Designer Pro: advanced cap table + ESOP modeling",
              "Governance Toolkit: complete pack for contracts, policies, and board structures",
              "Policy Guide Pro: ESG, HR, ethics, and data compliance templates",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've reached a stage where governance is no longer a red flag. Instead, it's becoming a competitive advantage. Investors will see your company as structured, trustworthy, and professional. The challenge now is to avoid complacency. Professional governance must evolve into scalable, institutional governance, strong enough to handle due diligence, Series A+ fundraising, and growth into new markets.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are a Structure Professional.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "Your company looks credible and well-structured. Now you must strengthen systems to scale.",
          },
          {
            kind: 'bullets',
            items: [
              "Adopt the Governance Dashboard Pro to systematize reporting.",
              "Finalize your ESOP using the Equity Designer Pro.",
              "Upgrade compliance with the Governance Toolkit.",
              "Book a Governance Systems Review with a Conncct advisor to check Series A readiness.",
            ],
          },
          {
            kind: 'callout',
            text: "Your next milestone: progress to G4R5 Governance Strategist, where governance moves from being about compliance to being a strategic enabler for growth and investor alignment.",
            tone: 'green',
          },
        ],
      },
    ],
  },

  /* G4R5 */
  {
    key: 'G4R5',
    group: 'G4',
    rCode: 'R5',
    pillar: 'Governance & Structure',
    stage: 'Semi-Ready Stage',
    tone: 'blue',
    profile: 'Governance Strategist',
    tagline: "You are a Governance Strategist, a founder who sees governance not as bureaucracy, but as a tool to accelerate growth and secure investor trust.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've built strong foundations: a clean equity structure, standardized contracts, functioning board meetings, and basic compliance. You're now ready to take governance to the next level by turning it into a strategic lever that builds credibility, attracts investors, and strengthens decision-making. At this stage, risk is moderate. Your governance is functional and credible, but to win investor confidence at larger rounds, you need to institutionalize processes, enhance transparency, and demonstrate forward-looking governance practices. You are a Governance Strategist, a founder who sees governance not as bureaucracy, but as a tool to accelerate growth and secure investor trust.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: "Your diagnostic results suggest:" },
          {
            kind: 'bullets',
            items: [
              "Cap table and ESOP are solid, with dilution modeling in place.",
              "Board structure exists, with agendas and minutes recorded.",
              "Policies cover key areas (HR, ethics, compliance, IP), though advanced ESG/governance policies may be missing.",
              "Compliance tracking is consistent, but not yet fully automated.",
              "Governance is seen as credible by early investors but may lack institutional depth for Series A/B rounds.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This is a strong stage, but investors will expect you to level up.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Elevate Board Governance",
                sub: [
                  "Move from founder-led discussions to structured board governance.",
                  "Include external advisors or investors in governance conversations.",
                  "Adopt a consistent reporting format for board decks.",
                ],
              },
              {
                title: "Advance Equity & ESOP Management",
                sub: [
                  "Formalize ESOP grants for early employees.",
                  "Benchmark ESOP pool size and terms against market standards.",
                  "Prepare equity documentation for diligence.",
                ],
              },
              {
                title: "Expand Compliance & ESG",
                sub: [
                  "Introduce ESG frameworks (environmental, social, governance).",
                  "Document anti-corruption, diversity, and sustainability policies.",
                  "Begin building your governance narrative for investors and partners.",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "At this stage, your platform unlocks strategic governance toolkits:",
          },
          {
            kind: 'bullets',
            items: [
              "Governance Toolkit Pro: board decks, decision matrices, governance policies",
              "ESOP Tracker Template: manage grants, vesting, and employee communications",
              "ESG Readiness Pack: policies for sustainability, ethics, and diversity",
              "Board Reporting Guide: templates for investor-ready governance reporting",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "Governance at this stage is not about checking boxes: it's about building trust and alignment. Strategic governance makes investors more confident, employees more motivated, and customers more assured of your long-term credibility. Your company is now viewed as semi-ready: strong enough to win interest, but in need of deeper systems to compete for top-tier capital.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are a Governance Strategist.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "You've mastered the basics. Now it's time to make governance a growth enabler.",
          },
          {
            kind: 'bullets',
            items: [
              "Adopt the Board Reporting Guide to elevate investor transparency.",
              "Use the ESOP Tracker to professionalize employee equity management.",
              "Implement the ESG Readiness Pack to strengthen your credibility.",
              "Book a Governance Strategy Review with a Conncct advisor to align governance with Series A/B expectations.",
            ],
          },
          {
            kind: 'callout',
            text: "Your next milestone: progress to G4R6 Governance Leader, where your governance maturity becomes a magnet for top-tier investors and strategic partners.",
            tone: 'green',
          },
        ],
      },
    ],
  },

  /* G4R6 */
  {
    key: 'G4R6',
    group: 'G4',
    rCode: 'R6',
    pillar: 'Governance & Structure',
    stage: 'Investor-Ready Stage',
    tone: 'green',
    profile: 'Governance Leader',
    tagline: "You are a Governance Leader, a founder who demonstrates maturity, foresight, and investor readiness through strong governance practices.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've built governance systems that are structured, consistent, and credible. Your equity structure is clean, your board is functional, and your compliance is managed professionally. At this stage, governance has shifted from being an obligation to being a strength that reassures investors and partners. You now operate with the discipline of a company ready for institutional capital. You are a Governance Leader, a founder who demonstrates maturity, foresight, and investor readiness through strong governance practices.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: "Your diagnostic results reflect:" },
          {
            kind: 'bullets',
            items: [
              "Cap table and ESOP are clean, transparent, and well-managed.",
              "Board structure is formalized, with cadence, agendas, and minutes recorded.",
              "Compliance systems are consistent, covering tax, legal, HR, and data privacy.",
              "Policies are comprehensive, extending to ESG and ethics.",
              "Reporting is regular, accurate, and aligned with investor expectations.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This level of discipline positions you as a company investors can trust.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Institutionalize Investor-Grade Governance",
                sub: [
                  "Adopt a quarterly board reporting cadence with standardized metrics.",
                  "Ensure reporting includes governance, risk, and ESG KPIs.",
                  "Begin preparing for audits where applicable.",
                ],
              },
              {
                title: "Strengthen Long-Term Governance Systems",
                sub: [
                  "Establish independent advisors or early non-executive directors.",
                  "Implement escalation and risk management frameworks.",
                  "Create governance continuity plans (succession, risk mitigation).",
                ],
              },
              {
                title: "Align Governance With Scaling Needs",
                sub: [
                  "Adapt ESOP for growth-stage hires.",
                  "Prepare governance documentation for Series A/B fundraising.",
                  "Build a governance narrative that demonstrates foresight.",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "At this stage, your platform unlocks investor-grade governance tools:",
          },
          {
            kind: 'bullets',
            items: [
              "Governance Toolkit Advanced: risk registers, board templates, governance KPIs",
              "ESOP Pro Template: manage scaling ESOP grants and reporting",
              "Governance Dashboard Pro: automated compliance & reporting workflows",
              "Investor Governance Pack: templates for quarterly board + investor reporting",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "Governance is now a strategic advantage. Investors will view your company as not only fundable but also scalable with reduced risk. This maturity makes you a stronger candidate for larger rounds and strategic partnerships. The key risk here is overconfidence: governance must keep evolving as your company grows in complexity.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are a Governance Leader.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "Your governance practices are investor-ready. Now they must scale with your growth.",
          },
          {
            kind: 'bullets',
            items: [
              "Adopt the Investor Governance Pack for quarterly reporting.",
              "Use the Governance Dashboard Pro to automate compliance.",
              "Formalize independent advisors/board members.",
              "Book a Governance Leadership Session with a Conncct advisor to align for Series A/B.",
            ],
          },
          {
            kind: 'callout',
            text: "Your next milestone: progress to G4R7 Governance Champion, where governance excellence positions you as a standout, attracting top-tier investors and global partners.",
            tone: 'green',
          },
        ],
      },
    ],
  },

  /* G4R7 */
  {
    key: 'G4R7',
    group: 'G4',
    rCode: 'R7',
    pillar: 'Governance & Structure',
    stage: 'Exceptional Stage',
    tone: 'green',
    profile: 'Governance Champion',
    tagline: "You are a Governance Champion, a founder whose governance practices make your company stand out as a professional, credible, and scalable business.",
    sections: [
      {
        title: 'Diagnosis Summary',
        blocks: [
          {
            kind: 'paragraph',
            text: "You've achieved world-class governance maturity. Your equity, policies, compliance, and board practices are not just in order, they're best-in-class. At this level, governance is no longer about avoiding mistakes: it's about accelerating growth, attracting top-tier investors, and building resilience. Your company is structured in a way that inspires confidence from all stakeholders: employees, partners, customers, and investors. You are a Governance Champion, a founder whose governance practices make your company stand out as a professional, credible, and scalable business.",
          },
        ],
      },
      {
        title: 'Why You Landed Here',
        blocks: [
          { kind: 'paragraph', text: "Your diagnostic results reflect governance excellence:" },
          {
            kind: 'bullets',
            items: [
              "Cap table and ESOP are transparent, automated, and benchmarked against market standards.",
              "Board governance is institutionalized: structured agendas, regular minutes, independent directors.",
              "Compliance systems are automated and proactive (tax, filings, data, IP, ESG).",
              "Policies are comprehensive, covering ESG, ethics, diversity, sustainability, and international compliance.",
              "Governance reporting is institutionalized: quarterly board packs, investor-ready dashboards.",
            ],
          },
          {
            kind: 'paragraph',
            text: "This is the level where investors not only trust you, they prefer you.",
          },
        ],
      },
      {
        title: 'Strategic Priorities (Next 90 Days)',
        blocks: [
          {
            kind: 'steps',
            items: [
              {
                title: "Elevate to Strategic Governance",
                sub: [
                  "Position governance as part of your investor narrative.",
                  "Publish ESG/governance reports to demonstrate leadership.",
                  "Use governance excellence to attract top-tier investors and global partners.",
                ],
              },
              {
                title: "Future-Proof Compliance & Risk Management",
                sub: [
                  "Adopt enterprise-level risk registers and compliance frameworks.",
                  "Ensure cross-border governance readiness (if expanding internationally).",
                  "Build succession planning and continuity strategies.",
                ],
              },
              {
                title: "Scale Board & Advisory Capacity",
                sub: [
                  "Add independent directors with global expertise.",
                  "Expand advisory councils to cover strategic functions (growth, ESG, tech).",
                  "Establish governance committees (audit, compensation, ESG).",
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Tools & Templates to Unlock',
        blocks: [
          {
            kind: 'paragraph',
            text: "At this elite stage, your platform unlocks strategic governance enablers:",
          },
          {
            kind: 'bullets',
            items: [
              "ESG Readiness Assessment (Advanced): align governance with global standards",
              "Governance Champion Toolkit: audit-ready packs, global compliance guides",
              "Strategic Partner Gateway: frameworks for investor + strategic partner alignment",
              "Board Excellence Pack: templates for committees, board packs, and succession planning",
            ],
          },
        ],
      },
      {
        title: 'What This Means for You',
        blocks: [
          {
            kind: 'paragraph',
            text: "You are no longer \"avoiding red flags.\" You are setting the gold standard. Governance is now part of your competitive edge. Investors see you as de-risked, scalable, and globally credible. Employees view you as stable and trustworthy. Partners view you as reliable. Your only challenge now is maintaining this excellence as you scale across geographies, industries, and investor bases.",
          },
        ],
      },
      {
        title: 'Call to Action',
        blocks: [
          { kind: 'callout', text: "You are a Governance Champion.", tone: 'blue' },
          {
            kind: 'paragraph',
            text: "Your governance inspires confidence. Now it's time to use it as a magnet for growth and capital.",
          },
          {
            kind: 'bullets',
            items: [
              "Showcase your governance excellence in investor decks and due diligence.",
              "Adopt the Governance Champion Toolkit to maintain institutional-grade practices.",
              "Expand your board with independent directors and committees.",
              "Book a Global Governance Strategy Session with a Conncct advisor to align with expansion and Series B+ requirements.",
            ],
          },
          {
            kind: 'callout',
            text: "Your next frontier: leveraging governance excellence as a strategic moat, one that ensures long-term sustainability, investor preference, and category leadership.",
            tone: 'green',
          },
        ],
      },
    ],
  },
];
